import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { HttpStatusCode } from '@angular/common/http'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators'
import { AdminPpa, AdminService } from '../../services/admin.service'
import {
  BLOCKABLE_CATEGORIES,
  PPA_DELETION_CATEGORIES,
  PpaDeletionCategory,
  retentionText,
} from '../../utils/ppa-deletion.util'
import { ppaPlace } from '../../utils/ppa.util'

export interface AdminPpaRemoveDialogData {
  ppa: AdminPpa
  retentionDays: number
}

/** A PPA to merge into - from GET /admin/ppas/:id/similar or a search. */
export type MergeTarget = Pick<AdminPpa, 'id' | 'name' | 'type_of_ppa' | 'corper_count' | 'review_count' | 'Locations'>

export type AdminPpaRemoveDialogResult =
  | { action: 'merge'; into: MergeTarget; note?: string }
  | { action: 'delete'; category: Exclude<PpaDeletionCategory, 'duplicate'>; note?: string; block_name: boolean }
  | undefined

/**
 * Removing a PPA from the admin: why (a category - always), and for a duplicate, which PPA to merge
 * it into (likely ones offered first, from its LGA) so its reviews and corp members aren't lost.
 * Returns the choice; AdminPpasComponent sends it.
 */
@Component({
  selector: 'app-admin-ppa-remove-dialog',
  templateUrl: './admin-ppa-remove-dialog.component.html',
  styleUrls: ['./admin-ppa-remove-dialog.component.css'],
})
export class AdminPpaRemoveDialogComponent implements OnInit, OnDestroy {
  readonly categories = PPA_DELETION_CATEGORIES
  readonly ppaPlace = ppaPlace
  readonly retention: string

  category = new FormControl<PpaDeletionCategory | null>(null)
  note = new FormControl('', { nonNullable: true })
  blockName = new FormControl(false, { nonNullable: true })
  search = new FormControl('', { nonNullable: true })

  similar: MergeTarget[] = []
  loadingSimilar = true
  searchResults: MergeTarget[] = []
  into: MergeTarget | null = null

  private destroy$ = new Subject<void>()

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AdminPpaRemoveDialogData,
    private dialogRef: MatDialogRef<AdminPpaRemoveDialogComponent, AdminPpaRemoveDialogResult>,
    private adminService: AdminService,
  ) {
    this.retention = retentionText(data.retentionDays)
  }

  ngOnInit(): void {
    this.adminService.getSimilarPpas(this.data.ppa.id).subscribe({
      next: (res) => {
        this.loadingSimilar = false
        this.similar = res.status === HttpStatusCode.Ok ? res.body?.ppas ?? [] : []
        // A likely duplicate: start on merging into the closest one.
        if (this.similar.length && !this.category.value) {
          this.category.setValue('duplicate')
          this.into = this.similar[0]
        }
      },
      error: () => {
        this.loadingSimilar = false
      },
    })
    this.search.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((text) => text.trim().length >= 2),
        switchMap((text) => this.adminService.getPpas({ search: text.trim(), limit: 8 })),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res) => {
          this.searchResults = (res.body?.ppas ?? []).filter((ppa: AdminPpa) => ppa.id !== this.data.ppa.id)
        },
        error: () => {
          this.searchResults = []
        },
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get isMerge(): boolean {
    return this.category.value === 'duplicate'
  }

  get canBlockName(): boolean {
    return !!this.category.value && BLOCKABLE_CATEGORIES.includes(this.category.value)
  }

  get noteRequired(): boolean {
    return this.category.value === 'other'
  }

  get canConfirm(): boolean {
    if (!this.category.value) return false
    if (this.isMerge) return !!this.into
    return !this.noteRequired || !!this.note.value.trim()
  }

  pick(target: MergeTarget): void {
    this.into = target
  }

  confirm(): void {
    if (!this.canConfirm || !this.category.value) return
    const note = this.note.value.trim() || undefined
    if (this.isMerge && this.into) {
      this.dialogRef.close({ action: 'merge', into: this.into, note })
      return
    }
    this.dialogRef.close({
      action: 'delete',
      category: this.category.value as Exclude<PpaDeletionCategory, 'duplicate'>,
      note,
      block_name: this.canBlockName && this.blockName.value,
    })
  }
}
